import { type ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { paths } from "config/paths";

const LOADING_FONT_SIZE = "16px";
const LOADING_TEXT_COLOR = "#666";

import { useAuth } from "./use-auth";

type ProtectedRouteProps = {
  children: ReactNode;
  /**
   * Optional redirect path when not authenticated.
   * Defaults to login path.
   */
  redirectTo?: string;
  /**
   * Whether to show a loading spinner while checking auth state.
   * Defaults to true.
   */
  showLoading?: boolean;
};

/**
 * LoadingSpinner component for auth checking
 */
const LoadingSpinner = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
      fontSize: LOADING_FONT_SIZE,
      color: LOADING_TEXT_COLOR,
    }}
  >
    Loading...
  </div>
);

/**
 * ProtectedRoute component that requires authentication to access
 *
 * This component:
 * 1. Checks if the user is authenticated
 * 2. Shows loading state while checking
 * 3. Redirects to login if not authenticated
 * 4. Preserves the intended destination for post-login redirect
 * 5. Renders children if authenticated
 */
export const ProtectedRoute = ({
  children,
  redirectTo = paths.login,
  showLoading = true,
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, refreshAuthState } = useAuth();
  const location = useLocation();

  useEffect(() => {
    refreshAuthState();
  }, [refreshAuthState]);

  if (isLoading && showLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate replace state={{ from: location.pathname }} to={redirectTo} />;
  }

  return <>{children}</>;
};
